import { createContext, useContext, useState, useEffect, useCallback } from "react";

const CartContext = createContext(null);

const CART_STORAGE_KEY = "app_cart";

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};

const loadCartFromStorage = () => {
  try {
    const saved = localStorage.getItem(CART_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (error) {
    console.error("Error loading cart from storage:", error);
  }
  return [];
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadCartFromStorage);

  // Sync to localStorage whenever items change
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error("Error saving cart to storage:", error);
    }
  }, [items]);

  // Listen for storage events from other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === CART_STORAGE_KEY && e.newValue) {
        try {
          const newItems = JSON.parse(e.newValue);
          if (Array.isArray(newItems)) {
            setItems(newItems);
          }
        } catch (error) {
          console.error("Error parsing cart from storage event:", error);
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      const existing = prev.find(
        (i) =>
          i.item_id === item.item_id &&
          JSON.stringify(i.extras) === JSON.stringify(item.extras)
      );

      let newItems;
      if (existing) {
        newItems = prev.map((i) =>
          i === existing ? { ...i, quantity: i.quantity + item.quantity } : i
        );
      } else {
        newItems = [...prev, item];
      }

      // Immediately save to localStorage to prevent race conditions
      try {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(newItems));
        console.log("Cart saved to localStorage:", newItems);
      } catch (error) {
        console.error("Error saving cart:", error);
      }

      return newItems;
    });
  }, []);

  const updateQuantity = useCallback((index, quantity) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, quantity } : item))
    );
  }, []);

  const removeItem = useCallback((index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    // Also immediately clear localStorage to ensure sync
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing cart from storage:", error);
    }
  }, []);

  const getTotal = useCallback(() => {
    return items.reduce((total, item) => {
      const itemPrice = parseFloat(item.price) || 0;
      const extrasTotal = item.extras?.reduce((sum, e) => sum + (parseFloat(e.price) || 0), 0) || 0;
      return total + (itemPrice + extrasTotal) * item.quantity;
    }, 0);
  }, [items]);

  const getItemCount = useCallback(() => {
    return items.reduce((count, item) => count + item.quantity, 0);
  }, [items]);

  const value = {
    items,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    getTotal,
    getItemCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
